import React, { useEffect, useState } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { X, Check, Minus, Plus } from 'lucide-react';
import type { Product, ProductSize, PosOptionGroup, SelectedOptionEntry } from '../../types/order';
import NumpadModal from './NumpadModal';
import { sizeChannelPrice } from '../../utils/pricing';
import { editedLineBasePrice, EditedLineOwn } from '../../utils/basketLineEdit';
import { extraSimpleIngredients, removableSimpleIngredients, simpleIngredientNames } from '../../utils/simpleIngredients';

/**
 * Self-contained product customization modal — used to capture size,
 * ingredients, extras, options, special instructions, and per-item discount
 * before adding a product to a basket / order.
 *
 * Currently consumed by dine-in. Takeaway still uses its inline modal for
 * historical reasons; future cleanup can swap takeaway here too.
 *
 * Internal state mirrors the takeaway flow exactly so behaviour stays
 * consistent for the cashier. Async data (warehouse ingredients, option
 * groups, materials by size) is loaded by the parent and passed in as props.
 */

export interface ProductCustomizeExtra {
  raw_material_id?: number;
  name: string;
  quantity: number;
  price: number;
}

export interface ProductCustomizeResult {
  size: ProductSize | null;
  removedIngredients: number[];
  removedIngredientNames: string[];
  addedExtras: ProductCustomizeExtra[];
  selectedOptions: SelectedOptionEntry[];
  specialInstructions: string;
  /** Percentage 0-100. 0 = no discount. */
  discount: number;
}

interface ProductCustomizeModalProps {
  open: boolean;
  product: Product;

  // Initial values (for edit-mode or pre-selected size)
  initialSize?: ProductSize | null;
  initialRemovedIngredients?: number[];
  initialAddedExtras?: ProductCustomizeExtra[];
  initialSelectedOptions?: SelectedOptionEntry[];
  initialSpecialInstructions?: string;
  initialDiscount?: number;
  /** Βάρος της γραμμής ζυγιζόμενου που επεξεργάζεται· η τιμή του υποσυνόλου το περιλαμβάνει. */
  weight?: number | null;
  /** Επεξεργασία γραμμής ανοιχτής τιμής/ζυγιζόμενου: το ποσό και το μέγεθός της (utils/basketLineEdit). */
  lineOwn?: EditedLineOwn | null;

  // Async-loaded data — parent fetches and passes in
  productIngredients?: Array<{
    id: number;
    name: string;
    is_default?: boolean;
    is_removable?: boolean;
    is_extra_available?: boolean;
    extra_price?: number | string;
    raw_material_id?: number;
  }>;
  productOptionGroups?: PosOptionGroup[];
  loadingMaterials?: boolean;

  /** When the user picks a different size, the parent often needs to refetch
   *  size-specific ingredients/materials. The shared component manages the
   *  selected-size state internally and just notifies the parent on change. */
  onSizeChange?: (size: ProductSize) => void;

  // Store config
  warehouseMode?: boolean;

  // Discount config (defaults loosely match the takeaway store-settings shape)
  discountPresets?: number[];
  maxDiscountPercent?: number;
  allowCustomDiscount?: boolean;

  // Toggles for sections (default all on)
  showDiscount?: boolean;
  showSpecialInstructions?: boolean;

  /** Επεξεργασία υπάρχουσας γραμμής καλαθιού: το κουμπί γράφει «Αποθήκευση» αντί για «Προσθήκη»
   *  (αλλιώς το πάτημα σε γραμμή μοιάζει να προσθέτει δεύτερο είδος). */
  isEditing?: boolean;

  onClose: () => void;
  onConfirm: (result: ProductCustomizeResult) => void;

  /** Order channel — picks the per-size price override (size-only stores). */
  channel?: 'take_away' | 'delivery' | 'dine_in';

  /** i18n namespace used for the section labels — defaults to 'takeaway' which
   *  is the most complete translation set today. */
  translationNamespace?: string;
}

const ProductCustomizeModal: React.FC<ProductCustomizeModalProps> = ({
  open,
  product,
  initialSize = null,
  initialRemovedIngredients = [],
  initialAddedExtras = [],
  initialSelectedOptions = [],
  initialSpecialInstructions = '',
  initialDiscount = 0,
  weight = null,
  lineOwn = null,
  productIngredients = [],
  productOptionGroups = [],
  loadingMaterials = false,
  onSizeChange,
  warehouseMode = true,
  discountPresets = [10, 20, 30, 50],
  maxDiscountPercent = 100,
  allowCustomDiscount = true,
  showDiscount = true,
  showSpecialInstructions = true,
  isEditing = false,
  onClose,
  onConfirm,
  channel = 'take_away',
  translationNamespace = 'takeaway',
}) => {
  const { t } = useTranslation([translationNamespace, 'common', 'products']);

  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(initialSize);
  const [removedIngredients, setRemovedIngredients] = useState<number[]>(initialRemovedIngredients);
  const [addedExtras, setAddedExtras] = useState<ProductCustomizeExtra[]>(initialAddedExtras);
  const [pendingOptions, setPendingOptions] = useState<SelectedOptionEntry[]>(initialSelectedOptions);
  const [specialInstructions, setSpecialInstructions] = useState<string>(initialSpecialInstructions);
  const [productDiscount, setProductDiscount] = useState<number>(initialDiscount);
  const [numpadOpen, setNumpadOpen] = useState(false);

  // Reset internal state whenever the product changes (parent typically
  // opens the modal with a fresh product per add-to-order interaction).
  useEffect(() => {
    if (!open) return;
    setSelectedSize(initialSize);
    setRemovedIngredients(initialRemovedIngredients);
    setAddedExtras(initialAddedExtras);
    setPendingOptions(initialSelectedOptions);
    setSpecialInstructions(initialSpecialInstructions);
    setProductDiscount(initialDiscount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, open]);

  if (!open || !product) return null;

  const otherCategoryLabel = t('otherCategory', { defaultValue: 'Other' });

  const toggleIngredientRemoval = (id: number) => {
    setRemovedIngredients(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Ίδιος τύπος με τις οθόνες (addToBasketWithSize / updateBasketItem): τιμή γραμμής
  // (μέγεθος, ή το προεπιλεγμένο χωρίς μέγεθος· × βάρος σε ζυγιζόμενο· το ποσό της γραμμής
  // ζυγιζόμενου/ανοιχτής τιμής, utils/basketLineEdit) + έξτρα + επιλογές.
  const unitPrice = editedLineBasePrice(product, selectedSize, weight, channel, lineOwn);
  const computeSubtotal = (): number => {
    const extrasSum = addedExtras.reduce((s, e) => s + e.price * e.quantity, 0);
    const optionsSum = pendingOptions.reduce((s, o) => s + (o.price_modifier || 0), 0);
    return unitPrice + extrasSum + optionsSum;
  };

  const subtotal = computeSubtotal();
  const finalTotal = subtotal * (1 - productDiscount / 100);

  // Απλά υλικά (warehouse = false): «χωρίς» = store_ingredient_id (όχι θέση στη λίστα) και
  // «Έξτρα» μόνο όσα δεν είναι ήδη μέσα δωρεάν — κανόνες στο utils/simpleIngredients.
  const removableSimple = !warehouseMode ? removableSimpleIngredients(product.simple_ingredients) : [];
  const extraSimple = !warehouseMode ? extraSimpleIngredients(product.simple_ingredients) : [];

  const collectRemovedNames = (): string[] => {
    if (!warehouseMode && product.simple_ingredients) {
      // Στη σειρά των ids: ο server κρατά το όνομα της ίδιας θέσης για ό,τι δεν βρει.
      return simpleIngredientNames(product.simple_ingredients, removedIngredients);
    }
    if (productIngredients && productIngredients.length > 0) {
      return productIngredients
        .filter(i => removedIngredients.includes(i.id))
        .map(i => i.name);
    }
    return [];
  };

  const handleConfirm = () => {
    // Required option groups must all have a selection.
    const missing = productOptionGroups.filter(
      g => g.is_required && !pendingOptions.some(o => o.group_id === g.id)
    );
    if (missing.length > 0) {
      const names = missing.map(g => g.name).join(', ');
      alert(t('pleaseSelect', { defaultValue: 'Please select: {{groups}}', groups: names }));
      return;
    }

    onConfirm({
      size: selectedSize,
      removedIngredients,
      removedIngredientNames: collectRemovedNames(),
      addedExtras,
      selectedOptions: pendingOptions,
      specialInstructions,
      discount: productDiscount,
    });
  };


  return (
    <div
      className="cashier-overlay fixed inset-0 flex items-center justify-center z-50 p-2 sm:p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
    >
      <div className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="cashier-dialog-header p-4 border-b border-line-strong bg-surface-2 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-t1">{product.name}</h2>
            <p className="text-t3 text-sm">
              {t('customizeOrder', { defaultValue: 'Customize order' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-12 h-12 rounded-xl bg-surface-3 hover:bg-surface-4 flex items-center justify-center active:scale-95 transition-all"
          >
            <X className="h-6 w-6 text-t2" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-4 overflow-y-auto flex-1 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Sizes */}
          {product.sizes && product.sizes.length > 1 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('size', { defaultValue: 'Size' })}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                {product.sizes.map(size => (
                  <button
                    key={size.id}
                    onClick={() => {
                      setSelectedSize(size);
                      onSizeChange?.(size);
                    }}
                    className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center ${
                      selectedSize?.id === size.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                        : 'border-line-strong hover:border-line-strong hover:bg-surface-2'
                    }`}
                  >
                    <div className="font-bold text-lg text-t1">{size.name}</div>
                    {/* Απόλυτη τιμή του μεγέθους (όχι «+»): ό,τι θα χρεωθεί χωρίς έξτρα. */}
                    <div className={`text-sm mt-1 font-medium ${selectedSize?.id === size.id ? 'text-blue-600 dark:text-blue-400' : 'text-t3'}`}>
                      {formatMoney(sizeChannelPrice(size, channel))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {loadingMaterials && (
            <div className="mb-6 text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
              <p className="text-sm text-t3 mt-2">{t('loadingMaterials', { defaultValue: 'Loading…' })}</p>
            </div>
          )}

          {/* Non-warehouse: simple ingredients (removable) — ταυτότητα = store_ingredient_id */}
          {!warehouseMode && removableSimple.length > 0 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('ingredients', { defaultValue: 'Ingredients' })}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {removableSimple.map((ing) => {
                    const id = Number(ing.store_ingredient_id);
                    const isRemoved = removedIngredients.includes(id);
                    return (
                      <button
                        key={`base-${id}`}
                        aria-pressed={isRemoved}
                        onClick={() => toggleIngredientRemoval(id)}
                        className={`p-3 rounded-xl border-2 transition-all active:scale-[0.97] text-left ${
                          isRemoved
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through'
                            : 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-t2'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isRemoved ? 'bg-red-500' : 'bg-green-500'}`}>
                            {isRemoved ? <X className="h-3 w-3 text-t1" /> : <Check className="h-3 w-3 text-t1" />}
                          </div>
                          <span className="font-medium">{ing.name}</span>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Non-warehouse: extras (grouped by category) — όχι τα προεπιλεγμένα 0 € (utils/simpleIngredients) */}
          {!warehouseMode && extraSimple.length > 0 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('extras', { defaultValue: 'Extras' })}
              </label>
              <div className="space-y-4">
                {(() => {
                  const list = extraSimple;
                  const categories = Array.from(new Set(list.map((i: any) => i.category || otherCategoryLabel)));
                  return categories.map(cat => (
                    <div key={cat}>
                      <p className="text-xs font-semibold text-t3 uppercase tracking-wider mb-2">{cat}</p>
                      <div className="space-y-2">
                        {list
                          .filter((i: any) => (i.category || otherCategoryLabel) === cat)
                          .map((ing: any, idx: number) => {
                            const existing = addedExtras.find(e => e.name === ing.name);
                            const qty = existing?.quantity || 0;
                            return (
                              <div key={idx} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl border-2 border-line-strong">
                                <div className="flex-1">
                                  <span className="font-medium text-t2">{ing.name}</span>
                                  {Number(ing.extra_price || 0) > 0 && (
                                    <span className="text-sm text-green-600 dark:text-green-400 ml-2">+{formatMoney(Number(ing.extra_price))}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      if (qty > 0) {
                                        if (qty === 1) setAddedExtras(addedExtras.filter(e => e.name !== ing.name));
                                        else setAddedExtras(addedExtras.map(e => e.name === ing.name ? { ...e, quantity: e.quantity - 1 } : e));
                                      }
                                    }}
                                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                                      qty > 0 ? 'bg-surface-4 text-t1' : 'bg-surface-3 text-t4'
                                    }`}
                                    disabled={qty === 0}
                                  >
                                    <Minus className="h-5 w-5" />
                                  </button>
                                  <span className="w-10 text-center font-bold text-lg text-t1">{qty}</span>
                                  <button
                                    onClick={() => {
                                      if (qty === 0) {
                                        setAddedExtras([...addedExtras, { raw_material_id: 0, name: ing.name, quantity: 1, price: Number(ing.extra_price || 0) }]);
                                      } else if (qty < 5) {
                                        setAddedExtras(addedExtras.map(e => e.name === ing.name ? { ...e, quantity: e.quantity + 1 } : e));
                                      }
                                    }}
                                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                                      qty < 5 ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t4'
                                    }`}
                                    disabled={qty >= 5}
                                  >
                                    <Plus className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Warehouse: removable ingredients */}
          {warehouseMode && !loadingMaterials && productIngredients.length > 0 && productIngredients.filter(i => i.is_default !== false && !i.is_extra_available && i.is_removable).length > 0 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('ingredients', { defaultValue: 'Ingredients' })}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {productIngredients.filter(i => i.is_default !== false && !i.is_extra_available && i.is_removable).map(ing => {
                  const isRemoved = removedIngredients.includes(ing.id);
                  return (
                    <button
                      key={ing.id}
                      onClick={() => toggleIngredientRemoval(ing.id)}
                      className={`p-3 rounded-xl border-2 transition-all active:scale-[0.97] text-left ${
                        isRemoved
                          ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through'
                          : 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-t2'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isRemoved ? 'bg-red-500' : 'bg-green-500'}`}>
                          {isRemoved ? <X className="h-3 w-3 text-t1" /> : <Check className="h-3 w-3 text-t1" />}
                        </div>
                        <span className="font-medium">{ing.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Warehouse: extras */}
          {warehouseMode && !loadingMaterials && productIngredients.filter(i => i.is_extra_available).length > 0 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('extras', { defaultValue: 'Extras' })}
              </label>
              <div className="space-y-2">
                {productIngredients.filter(i => i.is_extra_available).map(extra => {
                  const rmId = extra.raw_material_id || extra.id;
                  const existing = addedExtras.find(e => e.raw_material_id === rmId);
                  const qty = existing?.quantity || 0;
                  return (
                    <div key={rmId} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl border-2 border-line-strong">
                      <div className="flex-1">
                        <span className="font-medium text-t2">{extra.name}</span>
                        <span className="text-sm text-green-600 dark:text-green-400 ml-2">+{formatMoney(Number(extra.extra_price || 0))}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            if (qty > 0) {
                              if (qty === 1) setAddedExtras(addedExtras.filter(e => e.raw_material_id !== rmId));
                              else setAddedExtras(addedExtras.map(e => e.raw_material_id === rmId ? { ...e, quantity: e.quantity - 1 } : e));
                            }
                          }}
                          className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                            qty > 0 ? 'bg-surface-4 text-t1' : 'bg-surface-3 text-t4'
                          }`}
                          disabled={qty === 0}
                        >
                          <Minus className="h-5 w-5" />
                        </button>
                        <span className="w-10 text-center font-bold text-lg text-t1">{qty}</span>
                        <button
                          onClick={() => {
                            if (qty === 0) {
                              setAddedExtras([...addedExtras, { raw_material_id: rmId, name: extra.name, quantity: 1, price: Number(extra.extra_price || 0) }]);
                            } else if (qty < 5) {
                              setAddedExtras(addedExtras.map(e => e.raw_material_id === rmId ? { ...e, quantity: e.quantity + 1 } : e));
                            }
                          }}
                          className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                            qty < 5 ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t4'
                          }`}
                          disabled={qty >= 5}
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Option groups */}
          {productOptionGroups.length > 0 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('options', { defaultValue: 'Options' })}
              </label>
              <div className="space-y-5">
                {productOptionGroups.map(group => {
                  const count = group.choices.length;
                  const grid =
                    count <= 1 ? 'grid-cols-1' :
                    count === 2 ? 'grid-cols-2' :
                    count === 3 ? 'grid-cols-3' :
                    count === 4 ? 'grid-cols-2 sm:grid-cols-4' :
                    'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5';
                  return (
                    <div key={group.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-base font-bold text-t1">{group.name}</span>
                        {group.is_required && (
                          <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-semibold">
                            {t('required', { defaultValue: 'required' })}
                          </span>
                        )}
                        {group.selection_type === 'multiple' && (
                          <span className="text-xs text-t4">
                            {t('multiple', { defaultValue: 'multiple' })}
                          </span>
                        )}
                      </div>
                      <div className={`grid ${grid} gap-3`}>
                        {group.choices.map(choice => {
                          const isSelected = pendingOptions.some(o => o.group_id === group.id && o.choice_id === choice.id);
                          return (
                            <button
                              key={choice.id}
                              type="button"
                              onClick={() => {
                                const entry: SelectedOptionEntry = {
                                  group_id: group.id,
                                  group_name: group.name,
                                  choice_id: choice.id,
                                  choice_name: choice.name,
                                  price_modifier: choice.price_modifier,
                                };
                                if (group.selection_type === 'single') {
                                  setPendingOptions(prev => [...prev.filter(o => o.group_id !== group.id), entry]);
                                } else {
                                  setPendingOptions(prev =>
                                    isSelected
                                      ? prev.filter(o => !(o.group_id === group.id && o.choice_id === choice.id))
                                      : [...prev, entry]
                                  );
                                }
                              }}
                              className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center text-center ${
                                isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md' : 'border-line-strong hover:border-line-strong hover:bg-surface-2'
                              }`}
                            >
                              <span className="font-bold text-lg text-t1">{choice.name}</span>
                              {choice.price_modifier > 0 && (
                                <span className="text-sm mt-1 font-bold text-blue-600 dark:text-blue-400">+{formatMoney(choice.price_modifier)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Special instructions */}
          {showSpecialInstructions && (
            <div className="mb-5">
              <label htmlFor="instructions" className="text-base font-bold mb-3 block text-t2">
                {t('notes', { defaultValue: 'Notes' })}
              </label>
              <textarea
                id="instructions"
                value={specialInstructions}
                onChange={e => setSpecialInstructions(e.target.value)}
                placeholder={t('specialInstructions', { defaultValue: 'Special instructions for the kitchen' })}
                className="w-full p-4 border-2 border-line-strong bg-surface-2 text-t1 rounded-xl resize-none text-base focus:border-blue-500 focus:outline-none placeholder-t3"
                rows={2}
              />
            </div>
          )}

          {/* Per-item discount */}
          {showDiscount && maxDiscountPercent > 0 && (
            <div className="mb-5">
              <label className="text-base font-bold mb-3 block text-t2">
                {t('common:discount', { defaultValue: 'Discount' })}
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setProductDiscount(0)}
                  className={`h-12 px-5 rounded-xl font-bold transition-all active:scale-95 ${
                    productDiscount === 0 ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}
                >0%</button>
                {discountPresets.filter(p => p <= maxDiscountPercent).map(p => (
                  <button
                    key={p}
                    onClick={() => setProductDiscount(p)}
                    className={`h-12 px-5 rounded-xl font-bold transition-all active:scale-95 ${
                      productDiscount === p ? 'bg-blue-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                    }`}
                  >{p}%</button>
                ))}
                {allowCustomDiscount && (
                  <button
                    type="button"
                    onClick={() => setNumpadOpen(true)}
                    className="flex items-center gap-1 h-12 px-3 border-2 border-line-strong rounded-xl bg-surface-2 hover:border-blue-500 active:scale-95 transition-all"
                  >
                    <span className="w-10 text-center font-bold text-lg text-t1">
                      {productDiscount > 0 && !discountPresets.includes(productDiscount) ? productDiscount : '—'}
                    </span>
                    <span className="font-bold text-t3">%</span>
                  </button>
                )}
                {productDiscount > 0 && (
                  <span className="text-base text-green-600 dark:text-green-400 font-bold ml-2">
                    -{formatMoney((subtotal * productDiscount / 100))}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-line-strong bg-surface-1 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-t3 text-sm">
              <span>{selectedSize ? `${selectedSize.name}: ` : ''}{formatMoney(unitPrice)}</span>
              {addedExtras.length > 0 && (
                <span className="text-green-600 dark:text-green-400 ml-2">| {t('extras', { defaultValue: 'Extras' })}: +{formatMoney(addedExtras.reduce((s, e) => s + e.price * e.quantity, 0))}</span>
              )}
              {productDiscount > 0 && (
                <span className="text-red-600 dark:text-red-400 ml-2">| -{productDiscount}%</span>
              )}
            </div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {formatMoney(finalTotal)}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-14 rounded-xl border-2 border-line-strong font-bold text-t3 hover:bg-surface-2 active:scale-[0.98] transition-all"
            >
              {t('common:cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              onClick={handleConfirm}
              className="cashier-primary flex-[2] h-14 rounded-xl bg-blue-600 text-white font-bold text-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {isEditing ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {isEditing ? t('common:save', { defaultValue: 'Save' }) : t('add', { defaultValue: 'Add' })}
            </button>
          </div>
        </div>
      </div>

      <NumpadModal
        open={numpadOpen}
        initialValue={productDiscount}
        suffix="%"
        title={t('common:discount', { defaultValue: 'Έκπτωση' })}
        min={0}
        max={maxDiscountPercent}
        onClose={() => setNumpadOpen(false)}
        onConfirm={(v) => { setProductDiscount(v); setNumpadOpen(false); }}
      />
    </div>
  );
};

export default ProductCustomizeModal;
